/**
 * API Client Wrapper
 * Provides axios instance with interceptors for authentication and error handling
 * 
 * ⚠️ IMPORTANT: Per ADR-FE-001, this client only handles data fetching/display.
 * All business logic resides in backend services.
 */

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getApiConfig } from './config';

const apiConfig = getApiConfig();

// Validate API configuration
if (!apiConfig.baseURL) {
  const errorMsg = '[API Client] ERROR: baseURL is not configured. Please check your environment variables.';
  // eslint-disable-next-line no-console
  console.error(errorMsg, { config: apiConfig });
  throw new Error(errorMsg);
}

// Log API configuration in development
if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
  // eslint-disable-next-line no-console
  console.log('[API Client] Initialized with baseURL:', apiConfig.baseURL);
  // eslint-disable-next-line no-console
  console.log('[API Client] Environment:', apiConfig.environment);
}

/**
 * Axios instance configured with base URL and default headers
 */
export const apiClient = axios.create({
  baseURL: apiConfig.baseURL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request Interceptor: Add JWT token to all requests
 * 
 * Note: Firebase UID is NOT sent as a header because:
 * - API Gateway forwards user info in X-Apigateway-Api-Userinfo header
 * - Backend extracts Firebase UID from that header
 * - Frontend only needs to send the JWT token in Authorization header
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (typeof window !== 'undefined') {
      // Get token from localStorage (remember me) or sessionStorage (temporary session)
      // Check localStorage first (remember me), then sessionStorage
      const token = localStorage.getItem('flowrithm_jwt_token') || sessionStorage.getItem('flowrithm_jwt_token');

      if (config.headers) {
        // Add JWT token to Authorization header
        // API Gateway will validate this and forward user info to backend
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    }
    // TODO: For mobile, use secure storage (expo-secure-store or similar)

    // Log request URL and auth headers in development for debugging
    if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
      const fullUrl = config.baseURL ? `${config.baseURL}${config.url || ''}` : config.url;
      // eslint-disable-next-line no-console
      console.log(`[API Request] ${config.method?.toUpperCase()} ${fullUrl}`);
      // eslint-disable-next-line no-console
      console.log(`[API Request] Headers:`, {
        Authorization: config.headers?.Authorization ? 'Bearer ***' : 'Missing',
        baseURL: config.baseURL,
      });
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor: Handle errors globally
 */
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401: Unauthorized (token expired or invalid)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Try to refresh the token if we have Firebase auth
      if (typeof window !== 'undefined') {
        try {
          const { getFirebaseAuth } = await import('../config/firebase');
          const authInstance = getFirebaseAuth();

          if (authInstance?.currentUser) {
            const { getIdToken } = await import('firebase/auth');
            const newToken = await getIdToken(authInstance.currentUser, true); // Force refresh

            // Update token in storage
            const firebaseUid = authInstance.currentUser.uid;
            const rememberMe = localStorage.getItem('flowrithm_remember_me') === 'true';

            if (rememberMe) {
              localStorage.setItem('flowrithm_jwt_token', newToken);
              localStorage.setItem('flowrithm_firebase_uid', firebaseUid);
            } else {
              sessionStorage.setItem('flowrithm_jwt_token', newToken);
              sessionStorage.setItem('flowrithm_firebase_uid', firebaseUid);
            }

            // Update the authorization header and retry the request
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }

            // Retry the original request with new token
            return apiClient(originalRequest);
          }
        } catch (refreshError) {
          // Token refresh failed - clear storage and redirect to login
          // eslint-disable-next-line no-console
          console.error('Token refresh failed:', refreshError);
        }
      }

      // Clear stored token and Firebase UID
      if (typeof window !== 'undefined') {
        localStorage.removeItem('flowrithm_jwt_token');
        localStorage.removeItem('flowrithm_firebase_uid');
        localStorage.removeItem('flowrithm_remember_me');
        sessionStorage.removeItem('flowrithm_jwt_token');
        sessionStorage.removeItem('flowrithm_firebase_uid');
      }

      // Redirect to login (web only)
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      // TODO: For mobile, navigate to login screen
    }

    // Handle 503: Service Unavailable
    if (error.response?.status === 503) {
      // TODO: Show cached data warning
      // Service unavailable - will use cached data if available
    }

    // Handle network errors
    if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
      // Log detailed network error information
      const requestUrl = error.config?.baseURL
        ? `${error.config.baseURL}${error.config.url || ''}`
        : error.config?.url || 'unknown';

      // eslint-disable-next-line no-console
      console.error('[API Network Error]', {
        code: error.code,
        message: error.message,
        url: requestUrl,
        baseURL: error.config?.baseURL,
        method: error.config?.method,
      });

      // Check if baseURL is empty or invalid
      if (!error.config?.baseURL) {
        // eslint-disable-next-line no-console
        console.error('[API Network Error] baseURL is empty or undefined. Check API configuration.');
      }

      // TODO: Enable offline mode
      // Network error - offline mode will be enabled
    }

    return Promise.reject(error);
  }
);

/**
 * OpenAPI Generated Client Configuration
 * Uses the generated TypeScript client from OpenAPI spec
 */
import { Configuration, DefaultApi } from './generated';

export const apiConfiguration = new Configuration({
  basePath: apiConfig.baseURL,
  accessToken: () => {
    if (typeof window !== 'undefined') {
      // Check localStorage first (remember me), then sessionStorage
      return localStorage.getItem('flowrithm_jwt_token') || sessionStorage.getItem('flowrithm_jwt_token') || '';
    }
    return '';
  },
});

/**
 * Default API instance with all backend endpoints
 * Use this for making API calls to backend services
 */
export const api = new DefaultApi(apiConfiguration, undefined, apiClient);

// ---------------------------------------------------------------------------
// Admin API helpers
// Not part of the generated OpenAPI client — added manually for admin endpoints
// ---------------------------------------------------------------------------

export interface ListUsersParams {
  page?: number;
  size?: number;
  email?: string;
  role?: string;
  status?: string;
  is_active?: boolean;
  category_id?: string;
}

export interface ChangeUserRolePayload {
  role: 'user' | 'admin';
}

export interface UserCategoryPayload {
  name: string;
  description?: string;
  is_active?: boolean;
}

const adminApi = {
  /** List users with optional filters (admin use) */
  listUsers: (params: ListUsersParams = {}) =>
    apiClient.get('/api/v1/users', { params }),

  /** Change a user's role — admin only */
  changeUserRole: (userId: string, payload: ChangeUserRolePayload) =>
    apiClient.patch(`/api/v1/users/${userId}/role`, payload),

  /** Get all user categories */
  getUserCategories: () =>
    apiClient.get('/api/v1/users/categories'),

  /** Create a new user category */
  createUserCategory: (payload: UserCategoryPayload) =>
    apiClient.post('/api/v1/users/categories', payload),

  /** Update an existing user category */
  updateUserCategory: (categoryId: string, payload: UserCategoryPayload) =>
    apiClient.put(`/api/v1/users/categories/${categoryId}`, payload),

  /** Create a new supported broker */
  createSupportedBroker: (payload: any) =>
    apiClient.post('/api/v1/broker-accounts/supported-brokers', payload),

  /** Update a supported broker status */
  updateBrokerStatus: (brokerType: string, payload: { is_active: boolean }) =>
    apiClient.patch(`/api/v1/broker-accounts/supported-brokers/${brokerType}/status`, payload),
};

// Merge admin helpers onto the api object so usage is `api.listUsers(...)`, etc.
Object.assign(api, adminApi);
